import { Component, Inject, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../environments/environment';

export interface AssetDialogData {
  asset_id?: number;
  asset_tag?: string;
  asset_name?: string;
  category_id?: number;
  serial_number?: string;
  model?: string;
  manufacturer?: string;
  purchase_date?: string;
  purchase_cost?: number;
  salvage_value?: number;
  useful_life_years?: number;
  status?: string;
  location?: string;
  area_code?: number;
  assigned_employee_id?: number;
  warranty_expiry?: string;
  notes?: string;
}

@Component({
  selector: 'app-asset-dialog',
  templateUrl: './asset-dialog.component.html',
  styleUrls: ['./asset-dialog.component.scss']
})
export class AssetDialogComponent {
  assetForm: FormGroup;
  categories: any[] = [];
  areas: any[] = [];
  employees: any[] = [];
  isEditMode = false;
  loading = false;
  private categoriesLoaded = false;
  private areasLoaded = false;
  private employeesLoaded = false;

  constructor(
    private dialogRef: MatDialogRef<AssetDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AssetDialogData,
    private fb: FormBuilder,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {
    this.isEditMode = !!data.asset_id;
    this.initForm();
    this.loadDropdownData();
  }

  initForm(): void {
    this.assetForm = this.fb.group({
      asset_tag: [this.data.asset_tag || '', Validators.required],
      asset_name: [this.data.asset_name || '', Validators.required],
      category_id: [this.ensurePrimitive(this.data.category_id), Validators.required],
      serial_number: [this.data.serial_number || ''],
      model: [this.data.model || ''],
      manufacturer: [this.data.manufacturer || ''],
      purchase_date: [this.data.purchase_date || '', Validators.required],
      purchase_cost: [this.data.purchase_cost || 0, [Validators.required, Validators.min(0)]],
      salvage_value: [this.data.salvage_value || 0, [Validators.min(0)]],
      useful_life_years: [this.data.useful_life_years || 5, [Validators.required, Validators.min(1)]],
      status: [this.data.status || 'active', Validators.required],
      location: [this.data.location || ''],
      area_code: [this.ensurePrimitive(this.data.area_code)],
      assigned_employee_id: [this.ensurePrimitive(this.data.assigned_employee_id)],
      warranty_expiry: [this.data.warranty_expiry || ''],
      notes: [this.data.notes || '']
    });
    console.log('Form initialized with data:', this.data);
  }

  private ensurePrimitive(value: any): string | number {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return '';
    return value;
  }

  loadDropdownData(): void {
    // Load asset categories
    this.http.get(`${environment.apiServiceBaseUrl}/hawdco/assets/categories`).subscribe({
      next: (response: any) => {
        console.log('Categories API response:', response);
        console.log('Is array?', Array.isArray(response));
        // Handle response - ensure it's an array and create new reference
        if (Array.isArray(response)) {
          this.categories = JSON.parse(JSON.stringify(response));
        } else if (response && Array.isArray(response.data)) {
          this.categories = JSON.parse(JSON.stringify(response.data));
        } else {
          console.error('Response is not an array:', response);
          this.categories = [];
        }
        this.categoriesLoaded = true;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading categories:', error);
        this.categories = [];
        this.categoriesLoaded = true;
        this.cdr.detectChanges();
      }
    });

    // Load areas
    this.http.get(`${environment.apiServiceBaseUrl}/hawdco/areas`).subscribe({
      next: (data: any) => {
        console.log('Areas API response:', data);
        this.areas = Array.isArray(data) ? JSON.parse(JSON.stringify(data)) : (Array.isArray(data.data) ? JSON.parse(JSON.stringify(data.data)) : []);
      },
      error: (error) => {
        console.error('Error loading areas:', error);
        this.areas = [];
      }
    });

    // Load employees
    this.http.get(`${environment.apiServiceBaseUrl}/hawdco/employees`).subscribe({
      next: (data: any) => {
        console.log('Employees API response:', data);
        this.employees = Array.isArray(data) ? JSON.parse(JSON.stringify(data)) : (Array.isArray(data.data) ? JSON.parse(JSON.stringify(data.data)) : []);
      },
      error: (error) => {
        console.error('Error loading employees:', error);
        this.employees = [];
      }
    });
  }

  onSave(): void {
    if (this.assetForm.invalid) {
      this.assetForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const formData = this.assetForm.value;

    if (this.isEditMode) {
      formData.asset_id = this.data.asset_id;
    }

    this.dialogRef.close(formData);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  calculateDepreciation(): void {
    const purchaseCost = this.assetForm.get('purchase_cost')?.value || 0;
    const salvageValue = this.assetForm.get('salvage_value')?.value || 0;
    const usefulLife = this.assetForm.get('useful_life_years')?.value || 1;
    
    const depreciationRate = ((purchaseCost - salvageValue) / usefulLife).toFixed(2);
    
    // You could display this to the user or update a field
    console.log('Annual depreciation:', depreciationRate);
  }

  calculateAnnualDepreciation(): number {
    const purchaseCost = this.assetForm.get('purchase_cost')?.value || 0;
    const salvageValue = this.assetForm.get('salvage_value')?.value || 0;
    const usefulLife = this.assetForm.get('useful_life_years')?.value || 1;

    return (purchaseCost - salvageValue) / usefulLife;
  }

  trackByCategoryId(index: number, item: any): number {
    return item.categoryId || item.id || index;
  }

  trackByAreaCode(index: number, item: any): string {
    return item.area_code || index;
  }

  trackByEmployeeId(index: number, item: any): number {
    return item.employee_id || index;
  }
}
